/**
 * useAuthz — Authorization hook
 *
 * Exposes:
 *   can(action, module)  — checks the module-action permission matrix for the current user.
 *   dataScope            — translates org hierarchy position into a query filter level.
 *   systemRole           — the active SystemRole for the current user.
 *
 * Bridges:
 *   - src/permissions.ts       (CanonicalRole, normalizeRole)
 *   - src/config/permissions.ts (PERMISSION_MATRIX, SystemRole)
 *   - src/hooks/useDataScope.ts (RoleType → ScopeType)
 *   - src/context/AuthContext   (user)
 *   - src/contexts/WorkspaceContext (workspace.userRole)
 */

import { useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { normalizeRole } from '../permissions';
import {
  canonicalToSystemRole,
  canDo,
  type AppModule,
  type ModuleAction,
  type SystemRole,
} from '../config/permissions';
import { useDataScope } from './useDataScope';

// ─── Data Scope ─────────────────────────────────────────────

/**
 * Translated scope key aligned with the user-visible hierarchy labels:
 *   Diretor     → 'global'      (entire org)
 *   Gerente     → 'department'  (filtered by department_id)
 *   Coordenador → 'team'        (filtered by direct-report subtree)
 *   Analista    → 'individual'  (filtered by assigned_to === user.id)
 */
export type AuthzDataScope = 'global' | 'department' | 'team' | 'individual';

const ROLE_TYPE_TO_AUTHZ_SCOPE: Record<string, AuthzDataScope> = {
  director:    'global',
  manager:     'department',
  lead:        'team',
  operational: 'individual',
};

// ─── Hook ────────────────────────────────────────────────────

export interface UseAuthzResult {
  /** System role for the current user in the active org. */
  systemRole: SystemRole;

  /**
   * Returns true if the current user is allowed to perform `action` on `module`.
   * Stable reference — safe to use in dependency arrays.
   */
  can: (action: ModuleAction, module: AppModule) => boolean;

  /**
   * Hierarchical data-visibility scope derived from the user's org position.
   * Use this to decide query filter strategy — not for UI gating (use `can` for that).
   */
  dataScope: AuthzDataScope;

  /** True while scope profile is loading from DB. */
  isLoading: boolean;
}

export function useAuthz(): UseAuthzResult {
  const { user } = useAuth();
  const { workspace } = useWorkspace();
  const { roleType, isLoading } = useDataScope();

  const systemRole = useMemo<SystemRole>(() => {
    const rawRole = workspace?.userRole ?? user?.role ?? null;
    const canonical = normalizeRole(rawRole);
    return canonicalToSystemRole(canonical);
  }, [workspace?.userRole, user?.role]);

  const can = useCallback(
    (action: ModuleAction, module: AppModule): boolean => canDo(systemRole, action, module),
    [systemRole],
  );

  const dataScope = useMemo<AuthzDataScope>(
    () => ROLE_TYPE_TO_AUTHZ_SCOPE[roleType] ?? 'individual',
    [roleType],
  );

  return { systemRole, can, dataScope, isLoading };
}
